import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertOctagon, Loader2, Signal } from "lucide-react";

const STATE_INTENT = {
  active: { tone: "ok", label: "KATLAS node active" },
  unconfigured: { tone: "muted", label: "KATLAS unconfigured — production credential not set" },
  auth_rejected: { tone: "warn", label: "KATLAS node reachable; bearer token rejected" },
  unreachable: { tone: "warn", label: "KATLAS node unreachable" },
  node_error: { tone: "warn", label: "KATLAS node error" },
};

export default function KatlasReceiptPanel({ receipts }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    let alive = true;
    api.katlasStatus().then((s) => alive && setStatus(s)).catch(() => alive && setStatus({ status: "unreachable" }));
    return () => { alive = false; };
  }, []);

  const intent = status ? STATE_INTENT[status.status] || STATE_INTENT.unconfigured : null;

  return (
    <section className="border border-slate-200 bg-white p-7" data-testid="katlas-receipt-panel">
      <div className="flex items-start justify-between gap-6 mb-5">
        <div>
          <span className="eyebrow">KATLAS receipts</span>
          <h3 className="font-display text-2xl text-[var(--ink-heading)] mt-1">
            Signed receipts attached to this event
          </h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {!status ? (
            <Loader2 size={14} className="animate-spin text-[var(--ink-muted)]" />
          ) : status.status === "active" ? (
            <ShieldCheck size={14} className="text-emerald-700" />
          ) : (
            <AlertOctagon size={14} className="text-amber-700" />
          )}
          <span className="text-[var(--ink-mono)] font-mono-ui">
            {intent ? intent.label : "checking node…"}
          </span>
        </div>
      </div>

      {(!receipts || receipts.length === 0) ? (
        <p className="text-sm text-[var(--ink-muted)]" data-testid="receipts-empty">
          No receipts attached yet. A review action will request a KATLAS signature.
        </p>
      ) : (
        <ul className="space-y-2.5" data-testid="receipts-list">
          {receipts.map((hash) => (
            <li key={hash} className="flex items-center justify-between gap-4 border-t border-slate-100 pt-2.5 first:border-t-0 first:pt-0">
              <span className="font-mono-ui text-xs text-[var(--ink-mono)] truncate" data-testid={`receipt-hash-${hash}`}>
                {hash}
              </span>
              <Link
                to={`/r/${encodeURIComponent(hash)}`}
                className="text-xs text-[var(--ink-heading)] underline underline-offset-4 decoration-slate-300 hover:decoration-slate-900 whitespace-nowrap flex items-center gap-1"
                data-testid={`open-receipt-${hash}`}
              >
                <Signal size={12} /> Open receipt
              </Link>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-[var(--ink-muted)] mt-6 leading-relaxed">
        Receipt hashes and signed envelopes are produced by the live KATLAS
        node only. Stage 1 may show pending or unconfigured states.
      </p>
    </section>
  );
}
